/**
 * wordUp.js
 *
 * ES6 module — compatible with Webpack 4 (no optional chaining,
 * no nullish coalescing, no top-level await, no private class fields).
 *
 * Config priority (lowest → highest):
 *   DEFAULTS  →  options argument  →  data-wordup-* attributes
 *
 * Usage:
 *   import { initAll, WordUp } from './wordUp';
 *
 *   // 1. Auto-init — each element is fully configured by its own data-wordup-* attributes.
 *   const instances = initAll();
 *
 *   // 2. JS options only — no data-wordup-* attributes needed on the element.
 *   const instance = new WordUp('#hero', {
 *     speed:      800,
 *     transition: 'glitch',
 *     direction:  'random',
 *   });
 *
 *   // 3. JS options as base, data-wordup-* attributes override per-element.
 *   //    Useful with initAll() when you want shared defaults but
 *   //    per-element overrides via markup.
 *   const instances = initAll('.wordup', {
 *     speed:      1000,
 *     transition: 'fade',
 *   });
 *   // An element with data-wordup-transition="glitch" will still use glitch,
 *   // while elements without that attribute inherit transition: 'fade'.
 *
 * HTML:
 *   <!--
 *     All config is set via data-wordup-* attributes on the wrapper.
 *     Every attribute is optional — omitted ones fall back to the
 *     options argument, then to DEFAULTS.
 *
 *     data-wordup-speed              (Number ms)  How long each word stays visible.      [1500]
 *     data-wordup-anim-dur           (Number ms)  Duration of the in/out animation.      [400]
 *     data-wordup-direction          (String)     forward | backward | random | pingpong [forward]
 *     data-wordup-loop               (Boolean)    Restart after the last word.            [true]
 *     data-wordup-pause-on-hover     (Boolean)    Pause while the cursor is over the el.  [true]
 *     data-wordup-transition         (String)     fade | slideUp | slideDown | slideLeft
 *                                                 | slideRight | scale | flip | blur
 *                                                 | typewriter | glitch                  [slideUp]
 *     data-wordup-easing             (String)     Any valid CSS easing string.            [ease-out]
 *
 *     -- Scroll Spy (mirrors CountUp.js enableScrollSpy behaviour) --
 *
 *     data-wordup-enable-scroll-spy  (Boolean)    Don't auto-start. Instead, start when
 *                                                 the element scrolls into the viewport.
 *                                                 Pauses again when it scrolls out
 *                                                 (unless scrollSpyOnce is true).        [false]
 *     data-wordup-scroll-spy-delay   (Number ms)  Wait this long after the element enters
 *                                                 the viewport before starting.           [0]
 *     data-wordup-scroll-spy-once    (Boolean)    Fire once only. After the first in-view
 *                                                 event the IntersectionObserver is
 *                                                 disconnected and the instance runs
 *                                                 freely until loop:false stops it.       [false]
 *
 *     data-wordup-animate-first     (Boolean)    When true, the first span starts hidden
 *                                                 and animates in like every other word.
 *                                                 When false (default), the first span is
 *                                                 visible immediately on load.             [false]
 *   -->
 *   <div class="wordup"
 *     data-wordup-speed="1500"
 *     data-wordup-anim-dur="400"
 *     data-wordup-direction="forward"
 *     data-wordup-loop="true"
 *     data-wordup-pause-on-hover="true"
 *     data-wordup-transition="slideUp"
 *     data-wordup-easing="ease-out"
 *     data-wordup-enable-scroll-spy="true"
 *     data-wordup-scroll-spy-delay="200"
 *     data-wordup-scroll-spy-once="false"
 *   >
 *     <span>Design</span>
 *     <span>Build</span>
 *     <span>Ship</span>
 *   </div>
 *
 * Required CSS (add to your stylesheet):
 *   .wordup {
 *     display: grid;
 *     grid-template-areas: "cell";
 *     place-items: center;
 *   }
 *   .wordup span {
 *     grid-area: cell;
 *     opacity: 0;
 *   }
 */


// ─────────────────────────────────────────
// DEFAULTS
// The base layer. Every key here is a valid
// config property. Options and data-wordup-*
// attributes only need to supply what they
// want to change.
// ─────────────────────────────────────────
const DEFAULTS = {
  speed:           1500,       // (ms) dwell time per word
  animDur:         400,        // (ms) transition animation duration
  direction:       'forward',  // forward | backward | random | pingpong
  loop:            true,       // true → wrap around after last word
  pauseOnHover:    true,       // true → halt the timer on mouseenter / touchstart
  transition:      'slideUp',  // named key in TRANSITIONS map below
  easing:          'ease-out', // any CSS easing string

  // ── Scroll Spy ──────────────────────────
  // Mirrors the CountUp.js enableScrollSpy / scrollSpyOnce API.
  enableScrollSpy: false,      // true → don't auto-start; wait for viewport entry
  scrollSpyDelay:  0,          // (ms) delay after entering viewport before starting
  scrollSpyOnce:   false,      // true → observe only the first intersection, then disconnect

  // ── First word ──────────────────────────
  animateFirst:    false,      // true → first span starts hidden and animates in like all others
};


// ─────────────────────────────────────────
// ATTR_PREFIX
// The dataset key prefix after browser
// camelCasing. The HTML attribute namespace
// is "data-wordup-*"; the browser converts
// this to dataset.wordupSpeed etc.
//
// HTML attribute         → dataset key
// ─────────────────────────────────────────
// data-wordup-speed               → wordupSpeed
// data-wordup-anim-dur            → wordupAnimDur
// data-wordup-direction           → wordupDirection
// data-wordup-loop                → wordupLoop
// data-wordup-pause-on-hover      → wordupPauseOnHover
// data-wordup-transition          → wordupTransition
// data-wordup-easing              → wordupEasing
// data-wordup-enable-scroll-spy   → wordupEnableScrollSpy
// data-wordup-scroll-spy-delay    → wordupScrollSpyDelay
// data-wordup-scroll-spy-once     → wordupScrollSpyOnce
// ─────────────────────────────────────────


// ─────────────────────────────────────────
// readDataAttrs(element)
// Parses ONLY the data-wordup-* attributes
// that are actually present on the element.
// Returns a partial object — keys absent from
// the markup are simply not included, so they
// don't accidentally shadow the options arg.
//
// Type coercion:
//   speed, animDur, scrollSpyDelay → Number()
//   all Boolean flags              → any value except 'false' is truthy
//   everything else                → String (passed as-is to CSS / switch)
// ─────────────────────────────────────────
function readDataAttrs(el) {
  var d     = el.dataset;
  var attrs = {};

  // Only write a key when the attribute is present on the element.
  // This keeps the object sparse so Object.assign priority works correctly.
  if (d.wordupSpeed           !== undefined) { attrs.speed           = Number(d.wordupSpeed); }
  if (d.wordupAnimDur         !== undefined) { attrs.animDur         = Number(d.wordupAnimDur); }
  if (d.wordupDirection       !== undefined) { attrs.direction       = d.wordupDirection; }
  if (d.wordupLoop            !== undefined) { attrs.loop            = d.wordupLoop            !== 'false'; }
  if (d.wordupPauseOnHover    !== undefined) { attrs.pauseOnHover    = d.wordupPauseOnHover    !== 'false'; }
  if (d.wordupTransition      !== undefined) { attrs.transition      = d.wordupTransition; }
  if (d.wordupEasing          !== undefined) { attrs.easing          = d.wordupEasing; }
  if (d.wordupEnableScrollSpy !== undefined) { attrs.enableScrollSpy = d.wordupEnableScrollSpy !== 'false'; }
  if (d.wordupScrollSpyDelay  !== undefined) { attrs.scrollSpyDelay  = Number(d.wordupScrollSpyDelay); }
  if (d.wordupScrollSpyOnce   !== undefined) { attrs.scrollSpyOnce   = d.wordupScrollSpyOnce   !== 'false'; }
  if (d.wordupAnimateFirst    !== undefined) { attrs.animateFirst    = d.wordupAnimateFirst    !== 'false'; }

  return attrs;
}


// ─────────────────────────────────────────
// mergeConfig(element, options)
// Produces the final resolved config by
// merging the three layers in priority order:
//
//   DEFAULTS                ← lowest priority
//     ↑ overridden by
//   options (JS arg)        ← middle priority
//     ↑ overridden by
//   data-wordup-* attrs     ← highest priority
//
// Object.assign({}, ...) is a shallow merge
// which is correct here — all config values
// are primitives so there's nothing to
// deep-merge.
// ─────────────────────────────────────────
function mergeConfig(el, options) {
  return Object.assign(
    {},
    DEFAULTS,                // 1. base defaults
    options || {},           // 2. JS options (only defined keys override)
    readDataAttrs(el)        // 3. data-wordup-* attributes (only present attrs override)
  );
}


// ─────────────────────────────────────────
// TRANSITIONS
// A plain object acting as a named map.
// Each key is a transition name (matches
// the data-wordup-transition attribute value).
//
// Each transition object may define:
//
//   enter(span)
//     Sets the STARTING visual state of the
//     incoming span. Applied with transitions
//     disabled so it acts as keyframe "from".
//     Omit if you want the span to start
//     from its natural hidden state.
//
//   hide(span)
//     Applied to the OUTGOING span to animate
//     it away. CSS transitions are active.
//
//   show(span, word, cfg)
//     Applied to the INCOMING span to animate
//     it in. CSS transitions are active.
//     word — the original text string
//     cfg  — the instance config object
//            (used by text-based effects for
//            animDur timing)
//
// Adding a new transition:
//   Just add a new key with hide + show
//   (and optionally enter). No other code
//   needs to change.
// ─────────────────────────────────────────
const TRANSITIONS = {

  fade: {
    hide: function(s) { s.style.opacity = '0'; s.style.transform = 'none'; },
    show: function(s) { s.style.opacity = '1'; s.style.transform = 'none'; },
  },

  slideUp: {
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'translateY(40px)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'translateY(-40px)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'translateY(0)'; },
  },

  slideDown: {
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'translateY(-40px)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'translateY(40px)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'translateY(0)'; },
  },

  slideLeft: {
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'translateX(50px)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'translateX(-50px)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'translateX(0)'; },
  },

  slideRight: {
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'translateX(-50px)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'translateX(50px)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'translateX(0)'; },
  },

  scale: {
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'scale(1.4)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'scale(0.5)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'scale(1)'; },
  },

  flip: {
    // TIP: set perspective: 600px on the parent container for the best 3-D effect
    enter: function(s) { s.style.opacity = '0'; s.style.transform = 'rotateY(-90deg)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.transform = 'rotateY(90deg)'; },
    show:  function(s) { s.style.opacity = '1'; s.style.transform = 'rotateY(0deg)'; },
  },

  blur: {
    enter: function(s) { s.style.opacity = '0'; s.style.filter = 'blur(12px)'; },
    hide:  function(s) { s.style.opacity = '0'; s.style.filter = 'blur(12px)'; s.style.transform = 'none'; },
    show:  function(s) { s.style.opacity = '1'; s.style.filter = 'blur(0px)';  s.style.transform = 'none'; },
  },

  typewriter: {
    hide: function(s) { s.style.opacity = '0'; },
    show: function(s, word, cfg) {
      s.style.opacity = '1';
      s.textContent = '';
      var i = 0;
      var chars = word.split('');
      // Spread characters evenly across animDur; floor at 30ms per char
      var delay = Math.max(30, cfg.animDur / chars.length);
      var interval = setInterval(function() {
        s.textContent += chars[i];
        i += 1;
        if (i >= chars.length) { clearInterval(interval); }
      }, delay);
    },
  },

  glitch: {
    hide: function(s) { s.style.opacity = '0'; s.style.transform = 'skewX(15deg) scaleX(1.1)'; },
    show: function(s, word, cfg) {
      s.style.opacity = '1';
      s.style.transform = 'none';
      var glyphs = '!<>-_\\/[]{}—=+*^?#';
      var orig = word.split('');
      var ticks = 0;
      var interval = setInterval(function() {
        ticks += 1;
        if (ticks > 8) {
          s.textContent = word;
          clearInterval(interval);
          return;
        }
        // Randomly swap each character for a glyph on every tick
        s.textContent = orig.map(function(c) {
          return Math.random() > 0.5
            ? glyphs[Math.floor(Math.random() * glyphs.length)]
            : c;
        }).join('');
      }, cfg.animDur / 8);
    },
  },

};


// ─────────────────────────────────────────
// WordUp(container, options)
//
// container  {string|HTMLElement}  CSS selector or element reference.
// options    {object}              Optional. Any subset of DEFAULTS keys.
//                                  data-wordup-* attributes on the element
//                                  take precedence over anything set here.
//
// Config resolution order (lowest → highest priority):
//   DEFAULTS  →  options  →  data-wordup-* attributes
//
// Public API:
//   .play()          — resume / restart the timer
//   .pause()         — stop the timer
//   .next()          — jump one step forward
//   .prev()          — jump one step backward
//   .goTo(i)         — jump to a specific index
//   .handleScroll()  — manually re-check viewport visibility.
//                      Useful if you initialise before the DOM
//                      is fully painted and the element is already
//                      in view (mirrors CountUp's handleScroll()).
// ─────────────────────────────────────────
function WordUp(container, options) {

  // Accept either a selector string or a bare element
  if (typeof container === 'string') {
    container = document.querySelector(container);
  }

  if (!container) {
    throw new Error('WordUp: container element not found.');
  }

  // ── Resolve final config ─────────────────
  // Priority: DEFAULTS → options arg → data-wordup-* attributes
  var cfg = mergeConfig(container, options);

  var spans    = Array.from(container.querySelectorAll('span'));
  var original = spans.map(function(s) { return s.textContent.trim(); });

  var current    = 0;     // index of the currently visible span
  var pingDir    = 1;     // +1 or -1; toggled by pingpong direction
  var timer      = null;
  var hovered    = false;
  var delayTimer = null;  // pending scrollSpyDelay timeout handle
  var observer   = null;  // IntersectionObserver instance (scroll spy)

  // ── setTransition(span) ─────────────────
  // Writes the CSS transition shorthand onto
  // a span using the resolved cfg values.
  function setTransition(s) {
    s.style.transition = [
      'opacity '   + cfg.animDur + 'ms ' + cfg.easing,
      'transform ' + cfg.animDur + 'ms ' + cfg.easing,
      'filter '    + cfg.animDur + 'ms ' + cfg.easing,
    ].join(', ');
  }

  // ── Init: hide all spans, conditionally animate in the first ──
  // When animateFirst:false (default) span 0 is shown instantly — no flash on load.
  // When animateFirst:true span 0 starts in the entry state and animates in on boot,
  // matching the exact same animation every other word uses.
  spans.forEach(function(s, i) {
    setTransition(s);
    s.style.transform = 'none';
    s.style.opacity   = '0';             // all spans hidden to start
  });

  if (!cfg.animateFirst) {
    // Default: reveal span 0 immediately, no animation
    spans[0].style.opacity = '1';
  } else {
    // animateFirst: snap span 0 to its entry state (transitions off so no premature
    // animation), then immediately trigger show() so it animates in on load.
    // The timer's first tick will move to span 1 as normal.
    var tFirst = TRANSITIONS[cfg.transition] || TRANSITIONS.fade;
    if (tFirst.enter) {
      spans[0].style.transition = 'none';
      tFirst.enter(spans[0]);
      spans[0].getBoundingClientRect(); // force reflow — entry state is now painted
      setTransition(spans[0]);          // re-enable transition before show()
    }
    // Animate span 0 in — identical to how swap() reveals any other span
    tFirst.show(spans[0], original[0], cfg);
  }

  // ── nextIndex() ─────────────────────────
  // Returns the index of the next span based
  // on the configured direction.
  function nextIndex() {
    var n = spans.length;
    switch (cfg.direction) {

      case 'forward':
        return (current + 1) % n;

      case 'backward':
        return (current - 1 + n) % n;

      case 'random': {
        var r;
        // Re-draw until we get an index that differs from current
        do { r = Math.floor(Math.random() * n); } while (n > 1 && r === current);
        return r;
      }

      case 'pingpong': {
        var next = current + pingDir;
        if (next >= n - 1) { pingDir = -1; } // hit the end → reverse
        if (next <= 0)     { pingDir =  1; } // hit the start → reverse
        return Math.max(0, Math.min(n - 1, next));
      }

      default:
        return (current + 1) % n;
    }
  }

  // ── swap(toIndex) ───────────────────────
  // Transitions from `current` to `toIndex`.
  function swap(toIndex) {
    var t    = TRANSITIONS[cfg.transition] || TRANSITIONS.fade;
    var prev = current;
    current  = ((toIndex % spans.length) + spans.length) % spans.length;

    var sPrev = spans[prev];
    var sNext = spans[current];

    // Restore original text — typewriter / glitch may have changed it
    sNext.textContent = original[current];

    // If the transition defines an entry keyframe, snap to it instantly
    // (transition temporarily disabled so it acts as a start keyframe)
    if (t.enter) {
      sNext.style.transition = 'none';
      t.enter(sNext);
      sNext.getBoundingClientRect(); // force reflow → entry state is committed
    }

    // Re-enable transitions on both spans before animating
    setTransition(sPrev);
    setTransition(sNext);

    // Animate out the old span, animate in the new span
    t.hide(sPrev);
    t.show(sNext, original[current], cfg);
  }

  // ── step() ──────────────────────────────
  // Called on every timer tick.
  function step() {
    if (hovered) { return; }

    // Stop if loop:false and we've reached the boundary
    if (!cfg.loop) {
      var next  = nextIndex();
      var atEnd = (cfg.direction === 'forward'  && next <= current && current !== 0) ||
                  (cfg.direction === 'backward' && next >= current && current !== spans.length - 1);
      if (atEnd) { stop(); return; }
    }

    swap(nextIndex());
  }

  // ── Timer helpers ────────────────────────
  function start() {
    clearInterval(timer);
    timer = setInterval(step, cfg.speed);
  }

  function stop() {
    clearInterval(timer);
    timer = null;
  }

  // ── Scroll Spy ──────────────────────────
  // Mirrors CountUp.js enableScrollSpy logic:
  //
  //   • When the element enters the viewport, wait scrollSpyDelay ms
  //     then call start().
  //   • When it leaves the viewport (and scrollSpyOnce is false),
  //     cancel any pending delay and call stop().
  //   • When scrollSpyOnce is true, disconnect the observer after the
  //     first in-view event so the instance runs freely from then on.
  //
  // IntersectionObserver is supported in all modern browsers and IE is
  // excluded by the Babel target list in webpack.config.js. If you need
  // IE support, include the W3C IntersectionObserver polyfill.
  function initScrollSpy() {
    if (!cfg.enableScrollSpy) { return; }

    // Guard: if IntersectionObserver is unavailable, fall back to
    // normal auto-start so the instance still works.
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return;
    }

    observer = new IntersectionObserver(function(entries) {
      var entry = entries[0];

      if (entry.isIntersecting) {
        // ── Element has entered the viewport ──

        if (cfg.scrollSpyOnce) {
          // Disconnect immediately — we only care about the first sighting
          observer.disconnect();
          observer = null;
        }

        if (cfg.scrollSpyDelay > 0) {
          // Wait scrollSpyDelay ms, then start
          delayTimer = setTimeout(function() {
            delayTimer = null;
            start();
          }, cfg.scrollSpyDelay);
        } else {
          start();
        }

      } else {
        // ── Element has left the viewport ──
        // Only relevant when scrollSpyOnce is false (observer is still active)

        // Cancel any pending delay — the element left before the delay fired
        if (delayTimer !== null) {
          clearTimeout(delayTimer);
          delayTimer = null;
        }

        stop();
      }
    }, {
      // threshold: 0 — fires as soon as 1px enters or leaves the viewport,
      // matching CountUp's default behaviour.
      threshold: 0,
    });

    observer.observe(container);
  }

  // ── handleScroll() ──────────────────────
  // Public method — mirrors CountUp.handleScroll().
  // Call this after the DOM has rendered if you
  // initialise WordUp before the page is painted
  // and the element is already in view.
  function handleScroll() {
    if (!observer || !container) { return; }
    // Re-trigger the observer by unobserving and re-observing
    observer.unobserve(container);
    observer.observe(container);
  }

  // ── Hover pause ──────────────────────────
  if (cfg.pauseOnHover) {
    container.addEventListener('mouseenter', function() { hovered = true; });
    container.addEventListener('mouseleave', function() { hovered = false; });
    // Touch: pause on contact, resume 800ms after finger lifts
    container.addEventListener('touchstart', function() { hovered = true; },  { passive: true });
    container.addEventListener('touchend',   function() {
      setTimeout(function() { hovered = false; }, 800);
    }, { passive: true });
  }

  // ── Boot ─────────────────────────────────
  if (cfg.enableScrollSpy) {
    // Scroll spy mode: do NOT auto-start.
    // The IntersectionObserver callback handles start/stop.
    initScrollSpy();
  } else {
    // Normal mode: start immediately.
    start();
  }

  // ── Public API ───────────────────────────
  this.play  = start;
  this.pause = stop;

  this.handleScroll = handleScroll;

  this.next = function() {
    swap(nextIndex());
    if (timer) { start(); } // reset interval so dwell time is consistent
  };

  this.prev = function() {
    swap((current - 1 + spans.length) % spans.length);
    if (timer) { start(); }
  };

  this.goTo = function(i) {
    swap(i);
    if (timer) { start(); }
  };
}


// ─────────────────────────────────────────
// initAll(selector, options)
//
// selector  {string}  CSS selector for all containers to initialise.
//                     Defaults to '.wordup'.
// options   {object}  Shared JS options applied to every instance.
//                     Each element's data-wordup-* attributes still
//                     take precedence over these shared options.
//
// Returns an array of WordUp instances, one per matched element.
// ─────────────────────────────────────────
function initAll(selector, options) {
  var sel = selector || '.wordup';
  return Array.from(document.querySelectorAll(sel)).map(function(el) {
    return new WordUp(el, options);
  });
}


export { WordUp, initAll, DEFAULTS, TRANSITIONS };
export default WordUp;